/* eslint-disable react/no-array-index-key */
import React from "react";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import Alert from "@insightfulscience/atomic-react/Alert";
import {
	PostElementBlockQuote,
	PostElementImage,
	PostElementLine,
	PostElementListItem,
	PostElementOl,
	PostElementParagraph,
	PostElementTitleH1,
	PostElementTitleH2,
	PostElementTitleH3,
	PostElementTitleH4,
	PostElementTitleH5,
	PostElementTitleH6,
	PostElementUl,
} from "@insightfulscience/atomic-react/PostElement";
import Table from "@insightfulscience/atomic-react/Table";
import Tr from "@insightfulscience/atomic-react/Table/Tr";
import Td from "@insightfulscience/atomic-react/Table/Td";
import Button from "@insightfulscience/atomic-react/Button";
import Block from "@insightfulscience/atomic-react/Block";
import Tbody from "@insightfulscience/atomic-react/Table/Tbody";
import { generateHeadingId } from "./utils";
import WistiaVideoEmbed from "../../ui/WistiaVideoEmbed";
import YouTubeVideoEmbed from "../../ui/YouTubeVideoEmbed";
import PostEntryLink from "../../ui/PostEntryLink";
import RichTextLink from "../../ui/RichTextLink";
import { setRichTextImage } from "../../utils/imagesAPI";
import { CONTENT_TYPE } from "../../constants";

export const h1 = [];

const richTextTypes = {
	renderNode: {
		[BLOCKS.HEADING_1]: (node, children) => <PostElementTitleH1>{children}</PostElementTitleH1>,
		[BLOCKS.HEADING_2]: (node, children) => (
			<PostElementTitleH2 id={generateHeadingId(children)}>{children}</PostElementTitleH2>
		),
		[BLOCKS.HEADING_3]: (node, children) => (
			<PostElementTitleH3 spacing={{ mt: 0 }}>{children}</PostElementTitleH3>
		),
		[BLOCKS.HEADING_4]: (node, children) => (
			<PostElementTitleH4 spacing={{ mt: 0 }}>{children}</PostElementTitleH4>
		),
		[BLOCKS.HEADING_5]: (node, children) => (
			<PostElementTitleH5 spacing={{ mt: 0 }}>{children}</PostElementTitleH5>
		),
		[BLOCKS.HEADING_6]: (node, children) => (
			<PostElementTitleH6 spacing={{ mt: 0 }}>{children}</PostElementTitleH6>
		),
		[BLOCKS.PARAGRAPH]: (node, children) => <PostElementParagraph>{children}</PostElementParagraph>,
		[BLOCKS.QUOTE]: (node, children) => <PostElementBlockQuote>{children}</PostElementBlockQuote>,
		[BLOCKS.UL_LIST]: (node, children) => <PostElementUl>{children}</PostElementUl>,
		[BLOCKS.OL_LIST]: (node, children) => <PostElementOl>{children}</PostElementOl>,
		[INLINES.HYPERLINK]: (node, children) => <RichTextLink {...node}>{children}</RichTextLink>,
		[INLINES.ENTRY_HYPERLINK]: (node, children) => {
			const { data } = node;
			const id = data?.target?.fields?.isTutorial
				? CONTENT_TYPE.TUTORIAL
				: data?.target?.sys?.contentType?.sys?.id;
			const { slug } = data.target.fields || "";

			return (
				<PostEntryLink contentId={id} slug={slug}>
					{children}
				</PostEntryLink>
			);
		},
		[BLOCKS.LIST_ITEM]: node => {
			const UnTaggedChildren = documentToReactComponents(node, {
				renderNode: {
					[BLOCKS.PARAGRAPH]: (nodeParagraph, childrenParagraph) => childrenParagraph,
					[BLOCKS.UL_LIST]: (nodeUl, childrenUl) => <PostElementUl>{childrenUl}</PostElementUl>,
					[BLOCKS.OL_LIST]: (nodeOl, childrenOl) => <PostElementOl>{childrenOl}</PostElementOl>,
					[BLOCKS.LIST_ITEM]: (nodeLi, childrenLi) => (
						<PostElementListItem>{childrenLi}</PostElementListItem>
					),
					[INLINES.HYPERLINK]: (nodeLink, childrenLink) => (
						<RichTextLink {...nodeLink}>{childrenLink}</RichTextLink>
					),
				},
				renderText: children =>
					children
						.replace(/\u2028/g, "")
						.split("\n")
						.flatMap((text, i) => [i > 0 && <br key={`br_${i}`} />, text]),
			});

			return <>{UnTaggedChildren}</>;
		},
		[BLOCKS.HR]: () => <PostElementLine />,
		[BLOCKS.EMBEDDED_ASSET]: node => {
			const { data } = node;
			if (data.target.fields && data.target.fields.file.contentType === "text/html") {
				return <Alert status="error">EMBEDDED_ASSET_BLOCK</Alert>;
			}
			if (data.target.fields && data.target.fields.file) {
				return (
					<PostElementImage
						srcList={setRichTextImage(data.target.fields.file)}
						src={data.target.fields.file.url}
						alt={data.target.fields.title}
						caption={data.target.fields.description}
					/>
				);
			}
			return null;
		},
		[BLOCKS.EMBEDDED_ENTRY]: node => {
			const { data } = node;
			const { fields } = data.target;
			if (fields.videoUrl) {
				const { title, description, videoUrl } = fields;
				return (
					<>
						<WistiaVideoEmbed url={videoUrl} caption={description} />
						<YouTubeVideoEmbed url={videoUrl} title={title} caption={description} />
					</>
				);
			}
			if (fields.isButtonLinkEmbeded) {
				const { linkText, linkUrl } = fields;
				return (
					<>
						<Block sx={{ textAlign: "center" }}>
							<Button.Link href={linkUrl} styleType="secondary">
								{linkText}
							</Button.Link>
						</Block>
					</>
				);
			}
			return <Alert status="error">EMBEDDED_ENTRY_BLOCK</Alert>;
		},
		[BLOCKS.TABLE]: (node, children) => (
			<Table isBordered>
				<Tbody>{children}</Tbody>
			</Table>
		),
		[BLOCKS.TABLE_ROW]: (node, children) => <Tr>{children}</Tr>,
		[BLOCKS.TABLE_HEADER_CELL]: node => {
			return documentToReactComponents(node, {
				renderNode: {
					[BLOCKS.TABLE_HEADER_CELL]: (_, childrenParagraph) => {
						return (
							<Td isActive spacing={{ px: 2 }}>
								{childrenParagraph}
							</Td>
						);
					},
					[BLOCKS.PARAGRAPH]: (_, childrenParagraph) => {
						return (
							<PostElementParagraph spacing={{ my: 0 }}>{childrenParagraph}</PostElementParagraph>
						);
					},
				},
			});
		},
		[BLOCKS.TABLE_CELL]: node => {
			return documentToReactComponents(node, {
				renderNode: {
					[BLOCKS.TABLE_CELL]: (_, childrenParagraph) => (
						<Td spacing={{ px: 2 }}>{childrenParagraph}</Td>
					),
					[BLOCKS.PARAGRAPH]: (_, childrenParagraph) => (
						<PostElementParagraph spacing={{ my: 0 }}>{childrenParagraph}</PostElementParagraph>
					),
				},
			});
		},
	},
};

export default richTextTypes;
